import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Typography,
  Card,
  CardContent,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  Grid,
  CircularProgress,
  IconButton,
} from '@mui/material';
import {
  Search as SearchIcon,
  Launch as LaunchIcon,
  Description as EbrIcon,
  Clear as ClearIcon,
} from '@mui/icons-material';
import { statusColors } from '../App';
import { apiService } from '../services/apiService';

interface Batch {
  id: number;
  batch_number: string;
  status: string;
  product_name: string;
  product_code: string;
  mbr_number: string;
  line_name: string;
  batch_size_value: number;
  batch_size_unit: string;
  created_at: string;
  actual_start: string | null;
  actual_end: string | null;
  created_by: string;
}

interface Product {
  id: number;
  product_code: string;
  product_name: string;
}

const BatchSearch: React.FC = () => {
  const navigate = useNavigate();
  const [batches, setBatches] = useState<Batch[]>([]);
  const [allBatches, setAllBatches] = useState<Batch[]>([]);
  const [products, setProducts] = useState<Product[]>([]);
  const [loading, setLoading] = useState(false);
  const [searchQuery, setSearchQuery] = useState<string[]>([]);
  const [statusFilter, setStatusFilter] = useState<string[]>([]);
  const [productFilter, setProductFilter] = useState<string[]>([]);

  const statusOptions = [
    { value: '', label: 'All Statuses' },
    { value: 'pending', label: 'Pending' },
    { value: 'in_progress', label: 'In Progress' },
    { value: 'paused', label: 'Paused' },
    { value: 'completed', label: 'Completed' },
    { value: 'approved', label: 'Approved' },
    { value: 'rejected', label: 'Rejected' },
    { value: 'cancelled', label: 'Cancelled' },
  ];

  useEffect(() => {
    fetchProducts();
    fetchBatches();
  }, []);

  // Auto-filter on selection change
  useEffect(() => {
    if (allBatches.length === 0) return;
    let filtered = [...allBatches];
    if (searchQuery.length > 0) {
      filtered = filtered.filter(b => searchQuery.includes(b.batch_number));
    }
    if (statusFilter.length > 0) {
      filtered = filtered.filter(b => statusFilter.includes(b.status));
    }
    if (productFilter.length > 0) {
      filtered = filtered.filter(b => productFilter.some(pf => {
        const prod = products.find(p => String(p.id) === String(pf));
        return prod && b.product_code === prod.product_code;
      }));
    }
    setBatches(filtered);
  }, [searchQuery, statusFilter, productFilter, allBatches]); // eslint-disable-line react-hooks/exhaustive-deps

  const fetchProducts = async () => {
    try {
      const data = await apiService.getProducts();
      setProducts(data.results || data);
    } catch (error) {
      console.error('Error fetching products:', error);
    }
  };

  const fetchBatches = async () => {
    try {
      setLoading(true);
      const data = await apiService.getBatches();
      const list = data.results || data;
      setBatches(list);
      setAllBatches(list);
    } catch (error) {
      console.error('Error fetching batches:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = async () => {
    try {
      setLoading(true);
      // Client-side filtering from allBatches
      let filtered = [...allBatches];
      
      if (searchQuery.length > 0) {
        filtered = filtered.filter(b => searchQuery.includes(b.batch_number));
      }
      if (statusFilter.length > 0) {
        filtered = filtered.filter(b => statusFilter.includes(b.status));
      }
      if (productFilter.length > 0) {
        filtered = filtered.filter(b => productFilter.some(pf => {
          const prod = products.find(p => String(p.id) === String(pf));
          return prod && b.product_code === prod.product_code;
        }));
      }
      
      setBatches(filtered);
    } catch (error) {
      console.error('Error searching batches:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleClear = () => {
    setSearchQuery([]);
    setStatusFilter([]);
    setProductFilter([]);
    setBatches(allBatches);
  };

  const handleBatchClick = (batchId: number) => {
    navigate(`/batch-execution/${batchId}`);
  };

  const formatDate = (dateString: string | null) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleString();
  };

  const getStatusColor = (status: string) => {
    return statusColors[status as keyof typeof statusColors] || '#6B7280';
  };

  return (
    <Box>
      <Typography variant="h3" component="h1" gutterBottom>
        Batch Execution
      </Typography>

      <Typography variant="body1" color="textSecondary" sx={{ mb: 3 }}>
        Select a batch to open execution view
      </Typography>

      {/* Search Filters */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Grid container spacing={3} alignItems="center">
            <Grid item xs={12} md={4}>
              <FormControl fullWidth>
                <InputLabel>Batch Number</InputLabel>
                <Select
                  multiple
                  value={searchQuery}
                  label="Batch Number"
                  onChange={(e) => setSearchQuery(e.target.value as string[])}
                  renderValue={(selected) => (selected as string[]).join(', ')}
                >
                  {allBatches.map((b) => (
                    <MenuItem key={b.id} value={b.batch_number}>
                      {b.batch_number}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            
            <Grid item xs={12} md={3}>
              <FormControl fullWidth>
                <InputLabel>Status</InputLabel>
                <Select
                  multiple
                  value={statusFilter}
                  label="Status"
                  onChange={(e) => setStatusFilter(e.target.value as string[])}
                  renderValue={(selected) => (selected as string[]).map(s => statusOptions.find(o => o.value === s)?.label || s).join(', ')}
                >
                  {statusOptions.filter(o => o.value !== '').map((option) => (
                    <MenuItem key={option.value} value={option.value}>
                      {option.label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            
            <Grid item xs={12} md={3}>
              <FormControl fullWidth>
                <InputLabel>Product</InputLabel>
                <Select
                  multiple
                  value={productFilter}
                  label="Product"
                  onChange={(e) => setProductFilter(e.target.value as string[])}
                  renderValue={(selected) => (selected as string[]).map(id => {
                    const p = products.find(p => String(p.id) === String(id));
                    return p ? p.product_code : id;
                  }).join(', ')}
                >
                  {products.map((product) => (
                    <MenuItem key={product.id} value={product.id}>
                      {product.product_code} - {product.product_name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            
            <Grid item xs={12} md={2}>
              <Box display="flex" gap={1} flexDirection="column">
                <Button
                  fullWidth
                  variant="contained"
                  startIcon={<SearchIcon />}
                  onClick={handleSearch}
                  disabled={loading}
                >
                  Search
                </Button>
                <Button
                  fullWidth
                  variant="outlined"
                  startIcon={<ClearIcon />}
                  onClick={handleClear}
                  size="small"
                >
                  Clear
                </Button>
              </Box>
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      {/* Results */}
      <Card>
        <CardContent>
          <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
            <Typography variant="h5">
              Search Results
            </Typography>
            <Typography variant="body2" color="textSecondary">
              {batches.length} batches found
            </Typography>
          </Box>

          {loading ? (
            <Box display="flex" justifyContent="center" p={4}>
              <CircularProgress />
            </Box>
          ) : (
            <TableContainer>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell>Batch Number</TableCell>
                    <TableCell>MBR</TableCell>
                    <TableCell>Product</TableCell>
                    <TableCell>Line</TableCell>
                    <TableCell>Batch Size</TableCell>
                    <TableCell>Status</TableCell>
                    <TableCell>Created</TableCell>
                    <TableCell>Started</TableCell>
                    <TableCell>Completed</TableCell>
                    <TableCell>Created By</TableCell>
                    <TableCell>EBR</TableCell>
                    <TableCell>Execution</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {batches.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={12} align="center">
                        <Typography color="textSecondary">
                          No batches found matching your criteria
                        </Typography>
                      </TableCell>
                    </TableRow>
                  ) : (
                    batches.map((batch) => (
                      <TableRow key={batch.id} hover>
                        <TableCell>
                          <Typography variant="body2" fontWeight="medium">
                            {batch.batch_number}
                          </Typography>
                        </TableCell>
                        <TableCell>{batch.mbr_number}</TableCell>
                        <TableCell>
                          <Box>
                            <Typography variant="body2">
                              {batch.product_name}
                            </Typography>
                            <Typography variant="caption" color="textSecondary">
                              {batch.product_code}
                            </Typography>
                          </Box>
                        </TableCell>
                        <TableCell>{batch.line_name}</TableCell>
                        <TableCell>
                          {batch.batch_size_value} {batch.batch_size_unit}
                        </TableCell>
                        <TableCell>
                          <Chip
                            label={batch.status.replace('_', ' ')}
                            size="small"
                            sx={{
                              backgroundColor: getStatusColor(batch.status),
                              color: 'white',
                              textTransform: 'capitalize',
                              fontWeight: 'medium',
                            }}
                          />
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2">
                            {formatDate(batch.created_at)}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2">
                            {formatDate(batch.actual_start)}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2">
                            {formatDate(batch.actual_end)}
                          </Typography>
                        </TableCell>
                        <TableCell>{batch.created_by}</TableCell>
                        <TableCell>
                          <IconButton
                            size="small"
                            onClick={() => window.open(`/api/batches/${batch.id}/report/`, '_blank')}
                            sx={{ color: '#3B82F6' }}
                          >
                            <EbrIcon />
                          </IconButton>
                        </TableCell>
                        <TableCell>
                          <Button
                            variant="outlined"
                            size="small"
                            startIcon={<LaunchIcon />}
                            onClick={() => handleBatchClick(batch.id)}
                            sx={{ textTransform: 'none' }}
                          >
                            Open
                          </Button>
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </CardContent>
      </Card>
    </Box>
  );
};

export default BatchSearch;